import {
    state, api, $, escHtml, toast, formatDateTimeRO, customConfirm,
} from './core';
import { askText } from './dialogs';

export let partsOrderInited = false;

type OrderRequest = {
    id: number;
    machine_id: string | null;
    machine_name: string | null;
    machine_line: string | null;
    part_id: number | null;
    part_number: string | null;
    part_description: string;
    catalog_description: string | null;
    quantity: number;
    urgency: string;
    status: string;
    requested_by: string;
    requester_display_name: string | null;
    requested_at: string;
    notes: string | null;
    reviewed_by: string | null;
    reviewed_at: string | null;
    ordered_at: string | null;
    received_at: string | null;
    order_reference: string | null;
};

type PorLine = {
    id: string;
    product?: string;
    equipment: Array<{ id: string; name?: string; display_name?: string }>;
};

const STATUS_LABELS: Record<string, string> = {
    pending: 'În așteptare',
    ordered: 'Comandat',
    received: 'Primit',
    cancelled: 'Anulat',
};

const STATUS_CLASS: Record<string, string> = {
    pending: 'badge-yellow',
    ordered: 'badge-blue',
    received: 'badge-green',
    cancelled: 'badge-gray',
};

const URGENCY_LABELS: Record<string, string> = {
    normal: 'Normal',
    urgent: 'Urgent',
};

let _selectedPartId: number | null = null;
let _searchTimer: ReturnType<typeof setTimeout> | null = null;
let _porLinesCache: PorLine[] = [];
let _porSelectedLine = '';
let _porSelectedMachine = '';
let _porCurrentStep: 1 | 2 | 3 = 1;
let _listFilterLine = '';
let _listFilterMachine = '';
let _rowsCache: OrderRequest[] = [];
let _machineToLine: Record<string, string> = {};

function _canManageOrders(): boolean {
    const role = state.currentUser?.role || 'operator';
    return role === 'engineer' || role === 'master';
}

function _requesterLabel(r: OrderRequest): string {
    return r.requester_display_name || r.requested_by || '—';
}

function _partLabel(r: OrderRequest): string {
    if (r.part_number) {
        return `${escHtml(r.part_number)} — ${escHtml(r.part_description)}`;
    }
    return escHtml(r.part_description);
}

function _lineLabelForRequest(r: OrderRequest): string {
    if (r.machine_id && _machineToLine[r.machine_id]) {
        return _machineToLine[r.machine_id];
    }
    if (r.machine_line) {
        const first = r.machine_line.split(',')[0]?.trim();
        if (first) return first;
    }
    return '—';
}

function _statusBadge(r: OrderRequest): string {
    const statusCls = STATUS_CLASS[r.status] || 'badge-gray';
    const statusLabel = STATUS_LABELS[r.status] || r.status;
    return `<span class="badge ${statusCls}">${escHtml(statusLabel)}</span>`;
}

function _urgencyBadge(r: OrderRequest): string {
    if (r.urgency !== 'urgent') return '';
    return '<span class="badge badge-red" style="margin-left:4px">Urgent</span>';
}

function _machineLabel(r: OrderRequest): string {
    if (!r.machine_id) return '—';
    return `${escHtml(r.machine_id)}${r.machine_name ? ' — ' + escHtml(r.machine_name) : ''}`;
}

function _buildMachineToLineMap(): void {
    _machineToLine = {};
    _porLinesCache.forEach(pl => {
        (pl.equipment || []).forEach(eq => {
            _machineToLine[eq.id] = pl.id;
        });
    });
}

function _updatePorLineContext(): void {
    const ctx = $('#por-line-context');
    if (!ctx) return;
    const pl = _porLinesCache.find(l => l.id === _porSelectedLine);
    if (!pl) {
        ctx.textContent = '';
        return;
    }
    const product = pl.product ? ` — ${pl.product}` : '';
    ctx.textContent = `Linie: ${pl.id}${product}`;
}

function _clearPorMachineSelection(): void {
    _porSelectedMachine = '';
    const hidden = $('#por-machine') as HTMLSelectElement | null;
    if (hidden) hidden.value = '';
}

function _goBackToLine(): void {
    _clearPorMachineSelection();
    _renderPorLineTabs();
    _renderPorMachineChips();
    _updatePorLineContext();
    _setPorStep(1);
}

function _goBackToMachine(): void {
    _clearPorMachineSelection();
    _renderPorMachineChips();
    _setPorStep(2);
}

function _setPorStep(step: 1 | 2 | 3): void {
    _porCurrentStep = step;
    document.querySelectorAll('.por-wizard-step').forEach(el => {
        const btn = el as HTMLButtonElement;
        const n = parseInt(btn.dataset.porStep || '0', 10);
        btn.classList.toggle('por-step-active', n <= step);
        btn.classList.toggle('por-step-done', n < step);
        const isBackNav = (n === 1 && step >= 2) || (n === 2 && step >= 3);
        btn.classList.toggle('por-step-clickable', isBackNav);
        btn.disabled = n > step;
    });
    const lineTabs = $('#por-line-tabs') as HTMLElement | null;
    const machinePanel = $('#por-machine-panel') as HTMLElement | null;
    const partPanel = $('#por-part-panel') as HTMLElement | null;
    if (step === 1) {
        if (lineTabs) lineTabs.style.display = '';
        if (machinePanel) machinePanel.style.display = 'none';
        if (partPanel) partPanel.style.display = 'none';
    } else if (step === 2) {
        if (lineTabs) lineTabs.style.display = 'none';
        if (machinePanel) machinePanel.style.display = '';
        if (partPanel) partPanel.style.display = 'none';
        _updatePorLineContext();
    } else {
        if (lineTabs) lineTabs.style.display = 'none';
        if (machinePanel) machinePanel.style.display = 'none';
        if (partPanel) partPanel.style.display = '';
    }
}

async function _ensurePorLines(): Promise<void> {
    if (_porLinesCache.length) return;
    try {
        const struct = await api('/api/factory_structure');
        const lines: PorLine[] = (struct.production_lines || []).map((pl: PorLine) => ({
            id: pl.id,
            product: (pl as { product?: string }).product || '',
            equipment: pl.equipment || [],
        }));
        const utils = struct.utilities || {};
        Object.entries(utils as Record<string, { label?: string; machines?: PorLine['equipment'] }>).forEach(([uKey, u]) => {
            lines.push({
                id: uKey,
                product: u.label || uKey,
                equipment: u.machines || [],
            });
        });
        _porLinesCache = lines.filter(l => l.equipment.length > 0);
        if (!_porSelectedLine && _porLinesCache.length) {
            _porSelectedLine = _porLinesCache[0].id;
        }
        _buildMachineToLineMap();
    } catch (e) {
        console.warn('[parts-order] factory_structure failed', e);
    }
}

function _renderPorLineTabs(): void {
    const el = $('#por-line-tabs');
    if (!el) return;
    if (!_porLinesCache.length) {
        el.innerHTML = '<span class="por-empty-hint">Se încarcă liniile...</span>';
        return;
    }
    el.innerHTML = _porLinesCache.map(pl => {
        const active = pl.id === _porSelectedLine ? ' por-tab-active' : '';
        const sub = pl.product ? `<span class="por-tab-sub">${escHtml(pl.product)}</span>` : '';
        return `<button type="button" class="por-line-tab${active}" data-line="${escHtml(pl.id)}" role="tab">
            <span class="por-tab-id">${escHtml(pl.id)}</span>${sub}
        </button>`;
    }).join('');
    el.querySelectorAll('.por-line-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            _porSelectedLine = (btn as HTMLElement).dataset.line || '';
            _clearPorMachineSelection();
            _renderPorMachineChips();
            _updatePorLineContext();
            _setPorStep(2);
        });
    });
}

function _renderListLineTabs(): void {
    const el = $('#por-list-line-tabs');
    if (!el) return;
    if (!_porLinesCache.length) {
        el.innerHTML = '<span class="por-empty-hint">Se încarcă filtrele...</span>';
        return;
    }
    const allActive = !_listFilterLine ? ' por-tab-active' : '';
    let html = `<button type="button" class="por-line-tab${allActive}" data-line="" role="tab">
        <span class="por-tab-id">Toate</span>
    </button>`;
    html += _porLinesCache.map(pl => {
        const active = pl.id === _listFilterLine ? ' por-tab-active' : '';
        const sub = pl.product ? `<span class="por-tab-sub">${escHtml(pl.product)}</span>` : '';
        return `<button type="button" class="por-line-tab${active}" data-line="${escHtml(pl.id)}" role="tab">
            <span class="por-tab-id">${escHtml(pl.id)}</span>${sub}
        </button>`;
    }).join('');
    el.innerHTML = html;
    el.querySelectorAll('.por-line-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            _listFilterLine = (btn as HTMLElement).dataset.line || '';
            _listFilterMachine = '';
            _renderListMachineChips();
            void loadPartsOrderList();
        });
    });
}

function _renderListMachineChips(): void {
    const wrap = $('#por-list-machine-chips') as HTMLElement | null;
    if (!wrap) return;
    if (!_listFilterLine) {
        wrap.style.display = 'none';
        wrap.innerHTML = '';
        return;
    }
    const pl = _porLinesCache.find(l => l.id === _listFilterLine);
    if (!pl) {
        wrap.style.display = 'none';
        return;
    }
    wrap.style.display = '';
    const allActive = !_listFilterMachine ? ' por-chip-active' : '';
    let html = `<button type="button" class="por-machine-chip${allActive}" data-mid="" title="Toate mașinile">Toate</button>`;
    html += (pl.equipment || []).map(eq => {
        const lbl = (eq as { display_name?: string }).display_name || eq.name || eq.id;
        const active = eq.id === _listFilterMachine ? ' por-chip-active' : '';
        return `<button type="button" class="por-machine-chip${active}" data-mid="${escHtml(eq.id)}" title="${escHtml(eq.id)}">
            ${escHtml(lbl)}
        </button>`;
    }).join('');
    wrap.innerHTML = html;
    wrap.querySelectorAll('.por-machine-chip').forEach(btn => {
        btn.addEventListener('click', () => {
            _listFilterMachine = (btn as HTMLElement).dataset.mid || '';
            void loadPartsOrderList();
        });
    });
}

function _renderPorMachineChips(): void {
    const el = $('#por-machine-chips');
    if (!el) return;
    const pl = _porLinesCache.find(l => l.id === _porSelectedLine);
    if (!pl) {
        el.innerHTML = '';
        return;
    }
    el.innerHTML = (pl.equipment || []).map(eq => {
        const lbl = (eq as { display_name?: string }).display_name || eq.name || eq.id;
        const active = eq.id === _porSelectedMachine ? ' por-chip-active' : '';
        return `<button type="button" class="por-machine-chip${active}" data-mid="${escHtml(eq.id)}" title="${escHtml(eq.id)}">
            ${escHtml(lbl)}
        </button>`;
    }).join('');
    el.querySelectorAll('.por-machine-chip').forEach(btn => {
        btn.addEventListener('click', () => {
            _porSelectedMachine = (btn as HTMLElement).dataset.mid || '';
            const hidden = $('#por-machine') as HTMLSelectElement | null;
            if (hidden) hidden.value = _porSelectedMachine;
            const pl2 = _porLinesCache.find(l => l.id === _porSelectedLine);
            const eq = (pl2?.equipment || []).find(e => e.id === _porSelectedMachine);
            const hint = $('#por-selected-hint');
            if (hint) {
                hint.textContent = `${_porSelectedLine} → ${(eq as { display_name?: string })?.display_name || eq?.name || _porSelectedMachine}`;
            }
            _setPorStep(3);
        });
    });
}

async function _searchParts(q: string, machineId: string): Promise<void> {
    const box = $('#por-search-results') as HTMLElement | null;
    if (!box) return;
    if (q.length < 2) {
        box.style.display = 'none';
        box.innerHTML = '';
        return;
    }
    const params = new URLSearchParams({ q, limit: '12' });
    if (machineId) params.set('machine_id', machineId);
    const rows = await api(`/api/parts/search?${params.toString()}`);
    if (!rows.length) {
        box.innerHTML = '<div class="por-search-empty">Nicio potrivire în catalog — folosiți descrierea liberă.</div>';
        box.style.display = 'block';
        return;
    }
    box.innerHTML = rows.map((p: { id: number; part_number: string; description_ro: string; machine_id: string }) =>
        `<button type="button" class="por-search-item" data-id="${p.id}" data-label="${escHtml(p.part_number)} — ${escHtml(p.description_ro || '')}">
            <strong>${escHtml(p.part_number)}</strong>
            <span>${escHtml(p.description_ro || '')}</span>
            <small>${escHtml(p.machine_id || '')}</small>
        </button>`
    ).join('');
    box.style.display = 'block';
    box.querySelectorAll('.por-search-item').forEach(btn => {
        btn.addEventListener('click', () => {
            _selectedPartId = parseInt((btn as HTMLElement).dataset.id || '0', 10) || null;
            const desc = $('#por-description') as HTMLInputElement | null;
            if (desc) desc.value = (btn as HTMLElement).dataset.label || '';
            box.style.display = 'none';
            toast('Piesă selectată din catalog', 'success');
        });
    });
}

function _detailField(label: string, value: string): string {
    return `<div class="por-detail-item"><label>${escHtml(label)}</label><span>${value}</span></div>`;
}

function _detailTimeline(r: OrderRequest): string {
    const items: string[] = [];
    items.push(`<li><strong>Cerere creată</strong> — ${formatDateTimeRO(r.requested_at)} (${escHtml(_requesterLabel(r))})</li>`);
    /* Un ISTORIC se citește din momentele înregistrate, nu din starea de acum.
       Legarea de `status === 'ordered'` făcea ca „Marcat comandat" să dispară în
       clipa în care piesa era primită — adică exact când te-ai uita în istoric ca
       să vezi cât a durat de la comandă la livrare. Etapele parcurse rămân. */
    if (r.ordered_at) {
        items.push(`<li><strong>Marcat comandat</strong> — ${formatDateTimeRO(r.ordered_at)}${r.reviewed_by ? ' de ' + escHtml(r.reviewed_by) : ''}</li>`);
    } else if (r.reviewed_at && r.status === 'ordered') {
        // Cereri vechi, dinaintea coloanei `ordered_at`: cădem pe momentul deciziei.
        items.push(`<li><strong>Marcat comandat</strong> — ${formatDateTimeRO(r.reviewed_at)}${r.reviewed_by ? ' de ' + escHtml(r.reviewed_by) : ''}</li>`);
    }
    if (r.reviewed_at && r.status === 'cancelled') {
        items.push(`<li><strong>Anulat</strong> — ${formatDateTimeRO(r.reviewed_at)}${r.reviewed_by ? ' de ' + escHtml(r.reviewed_by) : ''}</li>`);
    }
    if (r.received_at) {
        items.push(`<li><strong>Primit</strong> — ${formatDateTimeRO(r.received_at)}${r.reviewed_by ? ' de ' + escHtml(r.reviewed_by) : ''}</li>`);
    }
    if (!items.length) return '';
    return `<h4 style="margin:16px 0 8px;font-size:.9rem;color:var(--accent)">Istoric status</h4><ul class="por-detail-timeline">${items.join('')}</ul>`;
}

function _detailActionsHtml(r: OrderRequest): string {
    if (!_canManageOrders()) return '';
    if (r.status === 'pending') {
        return `<button type="button" class="btn btn-primary por-detail-act" data-id="${r.id}" data-act="ordered">Marchează comandat</button>
                <button type="button" class="btn btn-outline por-detail-act" data-id="${r.id}" data-act="cancelled">Anulează</button>`;
    }
    if (r.status === 'ordered') {
        return `<button type="button" class="btn btn-success por-detail-act" data-id="${r.id}" data-act="received">Marchează primit</button>`;
    }
    return '';
}

function _openDetailModal(r: OrderRequest): void {
    const existing = document.getElementById('por-detail-overlay');
    existing?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'por-detail-overlay';
    overlay.className = 'modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Detalii cerere comandă');

    const notes = r.notes ? escHtml(r.notes) : '<span style="color:var(--text-muted)">—</span>';
    const catalog = r.catalog_description ? escHtml(r.catalog_description) : '—';
    const orderRef = r.order_reference ? escHtml(r.order_reference) : '—';

    overlay.innerHTML = `<div class="modal" style="max-width:640px;max-height:90vh;max-height:90dvh;overflow-y:auto">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:8px">
            <h3 style="margin:0;flex:1">${_partLabel(r)}${_urgencyBadge(r)}</h3>
            ${_statusBadge(r)}
        </div>
        <div class="por-detail-grid">
            ${_detailField('Cantitate', escHtml(String(r.quantity)))}
            ${_detailField('Urgență', escHtml(URGENCY_LABELS[r.urgency] || r.urgency))}
            ${_detailField('Linie', escHtml(_lineLabelForRequest(r)))}
            ${_detailField('Mașină', _machineLabel(r))}
            ${_detailField('Cod catalog', r.part_number ? escHtml(r.part_number) : '—')}
            ${_detailField('Descriere catalog', catalog)}
            ${_detailField('Cerut de', escHtml(_requesterLabel(r)))}
            ${_detailField('Utilizator', escHtml(r.requested_by || '—'))}
            ${_detailField('Data cererii', formatDateTimeRO(r.requested_at))}
            ${_detailField('Referință comandă', orderRef)}
            ${_detailField('Observații', notes)}
        </div>
        ${_detailTimeline(r)}
        <div class="por-detail-actions">
            ${_detailActionsHtml(r)}
            <button type="button" class="btn btn-outline" id="por-detail-close">Închide</button>
        </div>
    </div>`;

    document.body.appendChild(overlay);

    const close = () => overlay.remove();
    overlay.querySelector('#por-detail-close')?.addEventListener('click', close);
    overlay.addEventListener('click', e => {
        if (e.target === overlay) close();
    });
    document.addEventListener('keydown', function onKey(e) {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', onKey);
        }
    });

    overlay.querySelectorAll('.por-detail-act').forEach(btn => {
        btn.addEventListener('click', async () => {
            const id = parseInt((btn as HTMLElement).dataset.id || '0', 10);
            const act = (btn as HTMLElement).dataset.act || '';
            if (!id || !act) return;
            const ok = await _patchRequestStatus(id, act);
            if (ok) {
                close();
                await loadPartsOrderList();
            }
        });
    });
}

async function _patchRequestStatus(id: number, act: string): Promise<boolean> {
    const body: Record<string, string> = { status: act };
    if (act === 'ordered') {
        const ref = await askText('Marchează drept comandat', {
            label: 'Referință comandă (opțional):', okText: 'Comandat',
        });
        if (ref === null) return false;
        if (ref.trim()) body.order_reference = ref.trim();
    }
    if (act === 'cancelled') {
        if (!await customConfirm('Anulați cererea de comandă?', 'Anulare cerere')) return false;
    }
    try {
        await api(`/api/parts-order-requests/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        toast('Status actualizat', 'success');
        return true;
    } catch (e: unknown) {
        toast((e as Error)?.message || 'Eroare la actualizare', 'error');
        return false;
    }
}

function _bindRowClick(el: HTMLElement, r: OrderRequest): void {
    el.classList.add('por-row-clickable');
    el.setAttribute('tabindex', '0');
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', `Detalii cerere: ${r.part_description}`);
    const open = () => _openDetailModal(r);
    el.addEventListener('click', e => {
        if ((e.target as HTMLElement).closest('.por-act, .por-detail-act')) return;
        open();
    });
    el.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
        }
    });
}

function _renderTable(rows: OrderRequest[]): void {
    const wrap = $('#por-table-wrap') as HTMLElement | null;
    const cardList = $('#por-card-list') as HTMLElement | null;
    const tbody = $('#por-table tbody');
    const empty = $('#por-empty') as HTMLElement | null;
    if (!wrap || !tbody || !empty || !cardList) return;

    if (!rows.length) {
        /* Golirea e OBLIGATORIE, nu doar ascunderea. Pe telefon `.por-card-list`
           are `display: flex !important` (regula mobilă), care bate `display:none`
           scris aici în linie — deci un filtru fără niciun rezultat lăsa pe ecran
           cardurile căutării dinainte. Omul citea șase cereri care nu se potriveau
           cu filtrul pus. Fără rânduri în DOM, nu mai are ce rămâne vizibil. */
        tbody.innerHTML = '';
        cardList.innerHTML = '';
        wrap.style.display = 'none';
        cardList.style.display = 'none';
        empty.style.display = 'block';
        return;
    }
    empty.style.display = 'none';
    wrap.style.display = 'block';
    cardList.style.display = 'flex';

    tbody.innerHTML = rows.map(r => {
        const machine = _machineLabel(r);
        return `<tr data-id="${r.id}" class="por-row-clickable">
            <td>${_partLabel(r)}${_urgencyBadge(r)}</td>
            <td>${r.quantity}</td>
            <td>${machine}</td>
            <td>${escHtml(_requesterLabel(r))}</td>
            <td>${formatDateTimeRO(r.requested_at)}</td>
            <td>${_statusBadge(r)}</td>
            <td>${escHtml(r.notes || '')}</td>
        </tr>`;
    }).join('');

    cardList.innerHTML = rows.map(r => `<article class="por-request-card" data-id="${r.id}">
        <div class="por-card-top">
            <div class="por-card-part">${_partLabel(r)}${_urgencyBadge(r)}</div>
            ${_statusBadge(r)}
        </div>
        <div class="por-card-meta">
            <span>Cant: ${r.quantity}</span>
            <span>${_machineLabel(r)}</span>
            <span>${escHtml(_requesterLabel(r))}</span>
            <span>${formatDateTimeRO(r.requested_at)}</span>
        </div>
    </article>`).join('');

    tbody.querySelectorAll('tr').forEach((tr, i) => _bindRowClick(tr as HTMLElement, rows[i]));
    cardList.querySelectorAll('.por-request-card').forEach((card, i) => _bindRowClick(card as HTMLElement, rows[i]));
}

function _listQueryParams(): URLSearchParams {
    const statusSel = $('#por-filter-status') as HTMLSelectElement | null;
    const status = statusSel?.value || 'pending,ordered';
    const params = new URLSearchParams({
        status,
        exclude_test: '1',
    });
    if (_listFilterMachine) {
        params.set('machine_id', _listFilterMachine);
    } else if (_listFilterLine) {
        params.set('line_id', _listFilterLine);
    }
    return params;
}

export async function loadPartsOrderList(): Promise<void> {
    const params = _listQueryParams();
    const [rows, pendingRows] = await Promise.all([
        api(`/api/parts-order-requests?${params.toString()}`),
        api('/api/parts-order-requests?status=pending&exclude_test=1'),
    ]);
    _rowsCache = rows;
    _renderTable(rows);
    /* Sunt DOUĂ insigne cu același număr — una în meniu, una în antetul listei
       „Cereri existente" — și amândouă purtau același `id`. `querySelector` se
       oprește la prima, deci a doua rămânea pe zero, ascunsă, pentru totdeauna.
       Id-ul e acum unic (cel din meniu), iar clasa `.por-pending-badge` le
       adună pe toate: dacă mai apare una a treia, se actualizează singură. */
    const pending = pendingRows.length;
    document.querySelectorAll('.por-pending-badge').forEach(b => {
        (b as HTMLElement).textContent = String(pending);
        (b as HTMLElement).style.display = pending > 0 ? 'inline-flex' : 'none';
    });
}

export async function loadPartsOrder(): Promise<void> {
    if (!partsOrderInited) {
        const desc = $('#por-description') as HTMLInputElement | null;
        const searchBox = $('#por-search-results') as HTMLElement | null;
        desc?.addEventListener('input', () => {
            _selectedPartId = null;
            const q = (desc as HTMLInputElement).value.trim();
            if (_searchTimer) clearTimeout(_searchTimer);
            _searchTimer = setTimeout(() => {
                void _searchParts(q, _porSelectedMachine);
            }, 300);
        });
        document.addEventListener('click', e => {
            if (!searchBox || !desc) return;
            if (!(e.target as Node)?.contains?.(searchBox) && e.target !== desc) {
                searchBox.style.display = 'none';
            }
        });

        $('#por-back-line')?.addEventListener('click', () => _goBackToLine());
        $('#por-back-machine')?.addEventListener('click', () => _goBackToMachine());

        document.querySelectorAll('.por-wizard-step').forEach(el => {
            el.addEventListener('click', () => {
                const n = parseInt((el as HTMLElement).dataset.porStep || '0', 10);
                if (n === 1 && _porCurrentStep >= 2) _goBackToLine();
                else if (n === 2 && _porCurrentStep >= 3) _goBackToMachine();
            });
        });

        $('#por-submit')?.addEventListener('click', async () => {
            const part_description = ($('#por-description') as HTMLInputElement)?.value.trim();
            const quantity = parseFloat(($('#por-quantity') as HTMLInputElement)?.value || '1');
            const urgency = ($('#por-urgency') as HTMLSelectElement)?.value || 'normal';
            const notes = ($('#por-notes') as HTMLTextAreaElement)?.value.trim();
            if (!_porSelectedMachine) {
                toast('Selectați mașina', 'error');
                _setPorStep(2);
                return;
            }
            if (!part_description) {
                toast('Completați piesa de comandat', 'error');
                return;
            }
            if (!quantity || quantity <= 0) {
                toast('Cantitate invalidă', 'error');
                return;
            }
            try {
                await api('/api/parts-order-requests', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        machine_id: _porSelectedMachine,
                        part_id: _selectedPartId,
                        part_description,
                        quantity,
                        urgency,
                        notes: notes || null,
                    }),
                });
                toast('Cerere adăugată', 'success');
                _selectedPartId = null;
                if (desc) desc.value = '';
                if ($('#por-notes')) ($('#por-notes') as HTMLTextAreaElement).value = '';
                if ($('#por-quantity')) ($('#por-quantity') as HTMLInputElement).value = '1';
                _clearPorMachineSelection();
                _renderPorMachineChips();
                _setPorStep(2);
                await loadPartsOrderList();
            } catch (e: unknown) {
                toast((e as Error)?.message || 'Eroare la salvare', 'error');
            }
        });

        $('#por-refresh')?.addEventListener('click', () => void loadPartsOrderList());
        $('#por-filter-status')?.addEventListener('change', () => void loadPartsOrderList());

        partsOrderInited = true;
    }

    await _ensurePorLines();
    _renderPorLineTabs();
    _renderListLineTabs();
    _renderListMachineChips();
    if (_porSelectedLine && !_porSelectedMachine) {
        _setPorStep(1);
    } else if (_porSelectedLine && _porSelectedMachine) {
        _renderPorMachineChips();
        _setPorStep(3);
    }
    await loadPartsOrderList();
}
